import { BusinessObjectBase } from './base';
import { alternateStudentAvailabilitySchema } from '@schemas/business-objects/alternate-student-availability';
import type { AlternateStudent } from './alternate-student';
import type { Slot } from './slot';
import type { Id } from '../base-types';

export interface AlternateStudentAvailabilityData {
  readonly id: Id;
  readonly slotId: Id;
  readonly slot?: Slot;
  readonly alternateStudentId: Id;
  readonly alternateStudent?: AlternateStudent;
}

export interface AlternateStudentAvailability extends Readonly<AlternateStudentAvailabilityData> {}
export class AlternateStudentAvailability extends BusinessObjectBase {
  static readonly entityName = 'AlternateStudentAvailability' as const;
  static readonly plural = 'alternate-student-availabilities';
  static schema = alternateStudentAvailabilitySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<AlternateStudentAvailability[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new AlternateStudentAvailability(data));
  }

  static async findOne(
    filter?: any,
  ): Promise<AlternateStudentAvailability | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new AlternateStudentAvailability(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
  ): Promise<AlternateStudentAvailability> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new AlternateStudentAvailability(data);
  }

  static async create(
    data: Omit<AlternateStudentAvailabilityData, 'id'>,
  ): Promise<AlternateStudentAvailability> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new AlternateStudentAvailability(result);
  }

  static async createMany(
    data: Omit<AlternateStudentAvailabilityData, 'id'>[],
  ): Promise<AlternateStudentAvailability[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new AlternateStudentAvailability(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<AlternateStudentAvailabilityData>,
  ): Promise<AlternateStudentAvailability> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new AlternateStudentAvailability(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
